import { z } from "zod";

// =============================================================================
// Capacity consumption (Data for Analysis, per connection)
// Live tenant aggregate + per-app / per-datafile ranking of the last measured day.
// =============================================================================

// Qlik's `consumptionType` ("App Reload" / "App Import" / "Datafile"), normalized.
export const capacitySourceSchema = z.enum(["app_reload", "app_import", "datafile"]);
export type CapacitySource = z.infer<typeof capacitySourceSchema>;

// How the rankings are split in the UI: "app" is both app origins together
// (app_reload + app_import), "datafile" is the datafile origin.
export const capacityRowKindSchema = z.enum(["app", "datafile"]);
export type CapacityRowKind = z.infer<typeof capacityRowKindSchema>;

// `DATA_VOLUME_SOURCE_DETAIL_QDA`: "Max daily App Reload" = the app reloaded that
// day; "Previous App Reload" = carried forward from an earlier reload.
export const capacityMeasurementSchema = z.enum(["daily", "carry_forward"]);
export type CapacityMeasurement = z.infer<typeof capacityMeasurementSchema>;

export const capacitySnapshotStatusSchema = z.enum([
  "pending",
  "processing",
  "completed",
  "failed",
]);
export type CapacitySnapshotStatus = z.infer<typeof capacitySnapshotStatusSchema>;

// --- Start-collection input / job payload ---
export const collectCapacityInputSchema = z.object({
  connectionId: z.string().uuid(),
});
export type CollectCapacityInput = z.infer<typeof collectCapacityInputSchema>;

// Job payload (worker) — carries snapshotId so the worker persists to the right snapshot.
export const collectCapacityPayloadSchema = z.object({
  snapshotId: z.string().uuid(),
  connectionId: z.string().uuid(),
});
export type CollectCapacityPayload = z.infer<typeof collectCapacityPayloadSchema>;

// --- DTOs ---
export const capacitySegmentSchema = z.object({
  source: capacitySourceSchema,
  bytes: z.number(),
});
export type CapacitySegment = z.infer<typeof capacitySegmentSchema>;

export const capacityDailyTotalSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  source: capacitySourceSchema,
  bytes: z.number(),
});
export type CapacityDailyTotal = z.infer<typeof capacityDailyTotalSchema>;

export const capacitySnapshotSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  // links to the generic jobs row that drives execution (trigger only)
  jobId: z.string().uuid().nullable(),
  status: capacitySnapshotStatusSchema,
  error: z.string().nullable(),
  // Live tenant aggregate (`dataVolumeAggregated`); null until the worker reports it.
  localUsageBytes: z.number().nullable(),
  capacityLimitBytes: z.number().nullable(),
  globalPeakBytes: z.number().nullable(),
  globalPeakDate: z.string().nullable(), // YYYY-MM-DD
  closeToOverage: z.boolean(),
  qlikUpdateTime: z.string().nullable(), // ISO — when Qlik last refreshed the aggregate
  segments: z.array(capacitySegmentSchema),
  dailyTotals: z.array(capacityDailyTotalSchema),
  // The per-app/per-datafile detail comes from a separate (QVF) path that can
  // fail on its own: false = aggregate only, and detailError says why.
  detailAvailable: z.boolean(),
  detailError: z.string().nullable(),
  detailDate: z.string().nullable(), // YYYY-MM-DD — the day the ranking measures
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CapacitySnapshot = z.infer<typeof capacitySnapshotSchema>;

// One measured consumption row. `Datafile` rows carry datafileId/datafileName and
// no appId; app rows carry appId/appName/spaceName/reloadId.
export const capacityUsageRowSchema = z.object({
  id: z.string().uuid(),
  source: capacitySourceSchema,
  measurement: capacityMeasurementSchema.nullable(),
  appId: z.string().nullable(),
  appName: z.string().nullable(),
  spaceName: z.string().nullable(),
  datafileId: z.string().nullable(),
  datafileName: z.string().nullable(),
  reloadId: z.string().nullable(),
  sizeBytes: z.number(),
  chargeable: z.boolean(),
  usageDate: z.string().nullable(), // YYYY-MM-DD
});
export type CapacityUsageRow = z.infer<typeof capacityUsageRowSchema>;

// Server-side sort keys for the ranking list. "size" = the measured bytes desc
// (the default ranking), "name" = the displayed name asc — the same pair the
// storage analysis' file list offers.
export const capacityRowsSortSchema = z.enum(["size", "name"]);
export type CapacityRowsSort = z.infer<typeof capacityRowsSortSchema>;

// One space of a snapshot's datafile rows. Feeds the space filter's options and
// the first level of the per-space/per-folder view.
export const capacitySpaceUsageSchema = z.object({
  spaceName: z.string(),
  bytes: z.number(),
  fileCount: z.number(),
});
export type CapacitySpaceUsage = z.infer<typeof capacitySpaceUsageSchema>;

// --- Directory drill-down inside ONE space of the datafile ranking. Aggregated
// server-side, one level per request, mirroring the storage analysis' tree: a
// "dir" child carries its WHOLE subtree's bytes/files, a "file" child is one
// measured file. ---
export const capacityDirectoryChildSchema = z.object({
  name: z.string(), // the immediate path segment, not the full path
  kind: z.enum(["dir", "file"]),
  bytes: z.number(),
  fileCount: z.number(),
});
export type CapacityDirectoryChild = z.infer<typeof capacityDirectoryChildSchema>;

// --- Responses ---
export const capacitySnapshotResponseSchema = z.object({ data: capacitySnapshotSchema });
export type CapacitySnapshotResponse = z.infer<typeof capacitySnapshotResponseSchema>;

// A connection that was never collected has no snapshot at all — the "latest"
// read answers with data: null instead of 404, so the UI can show its
// first-run empty state without treating it as an error.
export const latestCapacitySnapshotResponseSchema = z.object({
  data: capacitySnapshotSchema.nullable(),
});
export type LatestCapacitySnapshotResponse = z.infer<typeof latestCapacitySnapshotResponseSchema>;

export const listCapacitySnapshotsResponseSchema = z.object({
  data: z.array(capacitySnapshotSchema),
  nextCursor: z.string().optional(), // opaque (encodes createdAt + id)
});
export type ListCapacitySnapshotsResponse = z.infer<typeof listCapacitySnapshotsResponseSchema>;

export const listCapacityUsageRowsResponseSchema = z.object({
  data: z.array(capacityUsageRowSchema),
  // opaque (encodes the active sort + its key + id); a cursor from a different
  // sort is rejected rather than paged from the wrong place
  nextCursor: z.string().optional(),
});
export type ListCapacityUsageRowsResponse = z.infer<typeof listCapacityUsageRowsResponseSchema>;

export const listCapacitySpacesResponseSchema = z.object({
  data: z.array(capacitySpaceUsageSchema),
  // Same population, rows whose space never resolved: unreachable by name, so
  // they cannot be a listed space — but dropping them silently would make the
  // per-space totals contradict the flat list. Reported as one bucket instead.
  unassigned: z.object({ bytes: z.number(), fileCount: z.number() }),
});
export type ListCapacitySpacesResponse = z.infer<typeof listCapacitySpacesResponseSchema>;

export const capacityDirectoryResponseSchema = z.object({
  children: z.array(capacityDirectoryChildSchema),
  // Totals of THIS level (the prefix's whole subtree), which is what the per-row
  // % share is measured against — not the space's or the snapshot's total.
  totalBytes: z.number(),
  totalFiles: z.number(),
  // The level held more children than one query returns; the listed ones are the
  // largest. A flag on the level, never an error — there is nothing to page.
  truncated: z.boolean().default(false),
});
export type CapacityDirectoryResponse = z.infer<typeof capacityDirectoryResponseSchema>;

export const startCapacityCollectionResponseSchema = z.object({
  data: capacitySnapshotSchema,
  created: z.boolean(),
});
export type StartCapacityCollectionResponse = z.infer<typeof startCapacityCollectionResponseSchema>;

// --- Worker -> API callback payloads (WORKER_SECRET routes) ---
export const saveCapacityAggregateSchema = z.object({
  snapshotId: z.string().uuid(),
  organizationId: z.string(),
  localUsageBytes: z.number().nullable(),
  capacityLimitBytes: z.number().nullable(),
  globalPeakBytes: z.number().nullable(),
  globalPeakDate: z.string().nullable(),
  closeToOverage: z.boolean(),
  qlikUpdateTime: z.string().nullable(),
  segments: z.array(capacitySegmentSchema),
});
export type SaveCapacityAggregate = z.infer<typeof saveCapacityAggregateSchema>;

// Worker streams the detail rows in batches. `rowKey` is a hash of the row's
// natural key, computed on the worker, so a re-delivered batch upserts instead
// of duplicating.
export const saveCapacityUsageRowsSchema = z.object({
  snapshotId: z.string().uuid(),
  organizationId: z.string(),
  rows: z
    .array(capacityUsageRowSchema.omit({ id: true }).extend({ rowKey: z.string().min(1) }))
    .max(500),
});
export type SaveCapacityUsageRows = z.infer<typeof saveCapacityUsageRowsSchema>;

export const saveCapacityDailyTotalsSchema = z.object({
  snapshotId: z.string().uuid(),
  organizationId: z.string(),
  dailyTotals: z.array(capacityDailyTotalSchema),
});
export type SaveCapacityDailyTotals = z.infer<typeof saveCapacityDailyTotalsSchema>;

export const finalizeCapacitySnapshotSchema = z.object({
  snapshotId: z.string().uuid(),
  organizationId: z.string(),
  // Partial degradation is success: the aggregate alone closes the snapshot as
  // "completed" with detailAvailable=false. "failed" means not even that came.
  status: z.enum(["completed", "failed"]),
  detailAvailable: z.boolean(),
  detailError: z.string().optional(),
  detailDate: z.string().optional(),
  error: z.string().optional(),
});
export type FinalizeCapacitySnapshot = z.infer<typeof finalizeCapacitySnapshotSchema>;

// --- Query strings (the API validates them; the CLI validates flags). ---
export const capacitySnapshotListQuerySchema = z
  .object({ connectionId: z.string().uuid().optional() })
  .passthrough();

export const capacityLatestQuerySchema = z
  .object({ connectionId: z.string().uuid() })
  .passthrough();

// kind = which ranking tab to page through ("app" covers both app origins);
// omitted means all of them. search/space/sort are applied server-side, since
// the ranking is paged by cursor — a client-side filter would only ever see the
// rows already fetched.
export const capacityRowsQuerySchema = z
  .object({
    kind: capacityRowKindSchema.optional(),
    sort: capacityRowsSortSchema.default("size"),
    search: z.string().trim().min(1).max(200).optional(),
    space: z.string().trim().min(1).max(200).optional(),
  })
  .passthrough();

// Which ranking's spaces to list: an app's space is where the app sits, a
// datafile's is where the file sits, so the two filters need different options.
export const capacitySpacesQuerySchema = z
  .object({ kind: capacityRowKindSchema.optional() })
  .passthrough();

// Directory drill-down inside one space. `prefix` is a path fragment, not a
// cursor — the service rejects one that does not name a directory.
export const capacityDirectoriesQuerySchema = z
  .object({
    space: z.string().trim().min(1).max(200),
    prefix: z.string().max(1000).default(""),
  })
  .passthrough();
